// API base - empty string means use same origin (works with Caddy HTTPS proxy)
const BASE = '';

import { enqueueOfflineOp } from './offlineSync';
import { authHeaders, setToken, setShop, endSession } from './session';

// These routes answer 401 when the password typed in is wrong, which is not the
// same as the pass having expired — they must never sign anyone out.
const CREDENTIAL_PATHS = ['/api/auth/admin-login', '/api/auth/supplier-login', '/api/auth/change-password'];

async function api(path, options = {}) {
  const isMutation = options.method && options.method !== 'GET';
  try {
    const res = await fetch(`${BASE}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...authHeaders(), ...options.headers },
      body: options.body ? JSON.stringify(options.body) : undefined,
    });
    if (!res.ok) {
      if (res.status === 401 && !CREDENTIAL_PATHS.includes(path)) endSession();
      const err = await res.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(err.error || 'Request failed');
    }
    if (res.headers.get('content-type')?.includes('text/csv')) {
      return res.text();
    }
    return res.json();
  } catch (e) {
    // TypeError = network failure (not an HTTP error). Queue mutations for later sync.
    if (e instanceof TypeError && isMutation) {
      const tempId = enqueueOfflineOp(path, options);
      // Return synthetic success so the component behaves as if the call worked
      if (options.method === 'POST') return { id: tempId || 'queued', ...(options.body || {}) };
      return {};
    }
    throw e;
  }
}

// Auth — both sign-ins return a pass that every later call carries
export const adminLogin = async (username, password) => {
  const res = await api('/api/auth/admin-login', { method: 'POST', body: { username, password } });
  setToken(res.token, 'admin');
  setShop(res.shop?.id, 'admin', res.shop?.name, res.shop?.currency);
  return res;
};
export const supplierLogin = async (name, password) => {
  const res = await api('/api/auth/supplier-login', { method: 'POST', body: { name, password } });
  setToken(res.token, 'supplier');
  setShop(res.shop?.id, 'supplier', res.shop?.name, res.shop?.currency);
  return res;
};
// Which shop this pass belongs to. Only needed for a session that was signed in before the screens showed
// the shop, which would otherwise have nothing to display until the next sign-in.
export const getMyShop = () => api('/api/my-shop');

// What this shop's prices are in. Only an admin may change it, and it converts nothing: it records what
// the numbers already are.
export const setShopCurrency = (currency) => api('/api/my-shop/currency', { method: 'PUT', body: { currency } });

export const changeAdminPassword = (current_password, new_password) =>
  api('/api/auth/change-password', { method: 'POST', body: { current_password, new_password } });

// Suppliers
export const getSuppliers = () => api('/api/suppliers');
// Passwords are never in the list; an admin asks for one supplier at a time and it is logged.
export const getSupplierPassword = (id) => api(`/api/suppliers/${id}/password`);
export const createSupplier = (name, password) => api('/api/suppliers', { method: 'POST', body: { name, password } });
export const updateSupplier = (id, data) => api(`/api/suppliers/${id}`, { method: 'PUT', body: data });
export const deleteSupplier = (id) => api(`/api/suppliers/${id}`, { method: 'DELETE' });

// Products
export const getProducts = () => api('/api/products');
export const createProduct = (data) => api('/api/products', { method: 'POST', body: data });
export const updateProduct = (id, data) => api(`/api/products/${id}`, { method: 'PUT', body: data });
export const deleteProduct = (id) => api(`/api/products/${id}`, { method: 'DELETE' });
export const importProducts = (products) => api('/api/products/import', { method: 'POST', body: { products } });
export const toggleStock = (id) => api(`/api/products/${id}/toggle-stock`, { method: 'POST' });
export const setDiscontinued = (id, discontinued) => api(`/api/products/${id}/discontinue`, { method: 'POST', body: { discontinued } });
export const assignProduct = (id, supplier_id, qty) => api(`/api/products/${id}/assign`, { method: 'POST', body: { supplier_id, qty } });
export const acknowledgeProduct = (id) => api(`/api/products/${id}/acknowledge`, { method: 'POST' });
export const confirmReceipt = (id, qty) => api(`/api/products/${id}/confirm-receipt`, { method: 'POST', body: { qty } });
export const exportProductsCSV = () => api('/api/export/products');

// Supplier-proposed variants. A variant of an item is its own product row here; these routes let a
// supplier tell us about a version we never listed, and let us open it to every other supplier.
export const getProductVariants = (id) => api(`/api/products/${id}/variants`);
export const proposeVariant = (id, body) => api(`/api/products/${id}/propose-variant`, { method: 'POST', body });
export const openVariant = (id, name) => api(`/api/products/${id}/open-variant`, { method: 'POST', body: { name } });
export const rejectVariant = (id) => api(`/api/products/${id}/reject-variant`, { method: 'POST' });
export const editVariant = (id, body) => api(`/api/products/${id}/variant`, { method: 'PATCH', body });
export const withdrawVariant = (id) => api(`/api/products/${id}/variant`, { method: 'DELETE' });

// Quotes
export const submitQuote = (product_id, supplier_id, price, available = true) => api('/api/quotes', { method: 'POST', body: { product_id, supplier_id, price, available } });
export const editQuote = (productId, supplierId, price, pending_review = false) => api(`/api/quotes/${productId}/${supplierId}`, { method: 'PUT', body: { price, pending_review } });

// Shipments
export const getShipments = () => api('/api/shipments');
export const createShipment = (data) => api('/api/shipments', { method: 'POST', body: data });
export const updateShipment = (id, data) => api(`/api/shipments/${id}`, { method: 'PUT', body: data });
export const deleteShipment = (id) => api(`/api/shipments/${id}`, { method: 'DELETE' });
export const addShipmentItem = (shipId, product_id, qty) => api(`/api/shipments/${shipId}/items`, { method: 'POST', body: { product_id, qty } });
export const removeShipmentItem = (shipId, productId) => api(`/api/shipments/${shipId}/items/${productId}`, { method: 'DELETE' });
export const receiveShipmentItem = (shipId, productId, qty) => api(`/api/shipments/${shipId}/items/${productId}/receive`, { method: 'POST', body: { qty } });
export const receiveAllShipment = (id) => api(`/api/shipments/${id}/receive-all`, { method: 'POST' });
export const quickReceive = (data) => api('/api/shipments/quick-receive', { method: 'POST', body: data });
export const editShipmentItemQty = (shipId, productId, ship_qty) => api(`/api/shipments/${shipId}/items/${productId}`, { method: 'PUT', body: { ship_qty } });
export const editReceivedQty = (shipId, productId, qty) => api(`/api/shipments/${shipId}/items/${productId}/received-qty`, { method: 'PUT', body: { qty } });
export const addReceivedItem = (shipId, data) => api(`/api/shipments/${shipId}/add-received-item`, { method: 'POST', body: data });
export const getShipmentPriceChanges = (id) => api(`/api/shipments/${id}/price-changes`);

// Conversations
export const getConversations = () => api('/api/conversations');
export const addConversation = (data) => api('/api/conversations', { method: 'POST', body: data });
export const deleteProductConversations = (productId) => api(`/api/conversations/product/${productId}`, { method: 'DELETE' });

// Shipment Comments
export const getShipmentComments = (shipmentId) => api(`/api/shipment-comments/${shipmentId}`);
export const getAllShipmentComments = () => api('/api/shipment-comments');
export const addShipmentComment = (data) => api('/api/shipment-comments', { method: 'POST', body: data });

// All products including deleted (for past orders)
export const getAllProducts = () => api('/api/products/all');

// Activity
export const getActivity = (supplier_id) => api(`/api/activity${supplier_id ? `?supplier_id=${supplier_id}` : ''}`);
export const getProductActivity = (product_id) => api(`/api/activity?product_id=${product_id}`);

// Order History
export const getOrderHistory = (product_id) => api(`/api/order-history${product_id ? `?product_id=${product_id}` : ''}`);

// Reorder Prediction
export const getReorderPrediction = () => api('/api/reorder-prediction');

// Reports
export const reportsAuth = (password) => api('/api/reports/auth', { method: 'POST', body: { password } });
export const reportsChangePassword = (oldPassword, newPassword) => api('/api/reports/change-password', { method: 'POST', body: { oldPassword, newPassword } });
export const getReportsData = () => api('/api/reports/data');

// Mission Control link (card connuv2g86pr stage 2). The app side, so an admin session: the three
// /api/mc endpoints are the worker's and carry the shared secret, and the browser never sees them.
export const getMcLink = () => api('/api/my-shop/mc-link');
export const createMcLinkCode = (workspace_id, return_url) =>
  api('/api/my-shop/mc-link/code', { method: 'POST', body: { workspace_id, return_url } });
export const unlinkMc = () => api('/api/my-shop/mc-link/unlink', { method: 'POST' });

// Settings
export const getSetting = (key) => api(`/api/settings/${key}`);

// Which build the server is serving. Public, so the login screen notices a release too.
export const getVersion = () => api('/api/version');
export const setSetting = (key, value) => api(`/api/settings/${key}`, { method: 'PUT', body: { value } });